import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { Button } from '@material-ui/core';

import SelectField from 'components/SelectField';
import SearchBar from 'components/SearchBar';
import { mapToSelectOptions } from 'utils/select';
import { getVenuesWithStockModuleActive } from 'services/venuesServices';
import { getVenueWarehouses } from 'services/stockWarehouseServices';
import Venue from 'types/models/Venue';
import { StockWarehouse } from 'types/models/StockReceipt';
import AppContext from 'contexts/AppContext';
import useSetupInitialState from 'hooks/useSetupInitialState';
import UserRoleType from 'types/enums/UserRoleType';

export interface StockCatalogFilters {
  venueId: string;
  warehouseIds: string[];
  searchTerm: string;
  status: string[];
}

type Props = {
  onSearch: (filters: StockCatalogFilters) => void;
  onExport: (filters: StockCatalogFilters) => void;
  isLoading: boolean;
  isExporting: boolean;
};

export default ({ onSearch, onExport, isLoading, isExporting }: Props): JSX.Element => {
  const { t } = useTranslation();

  const { authData } = React.useContext(AppContext);
  const entity = useSetupInitialState();
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const [venues, setVenues] = React.useState<Venue[]>([]);
  const [warehouses, setWarehouses] = React.useState<StockWarehouse[]>([]);

  const [isLoadingVenues, setIsLoadingVenues] = React.useState(true);
  const [isLoadingWarehouses, setIsLoadingWarehouses] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const formik = useFormik<StockCatalogFilters>({
    enableReinitialize: true,
    initialValues: {
      venueId: entity?.id ?? '',
      warehouseIds: [],
      searchTerm: '',
      status: [],
    },
    onSubmit: (values) => {
      onSearch({
        venueId: values.venueId,
        warehouseIds: values.warehouseIds,
        searchTerm: values.searchTerm.trim(),
        status: values.status ?? [],
      });
    },
  });

  React.useEffect(() => {
    if (isVenueEditor) {
      setIsLoadingVenues(false);
      return;
    }

    let mounted = true;

    async function loadVenues() {
      setIsLoadingVenues(true);

      try {
        const response = await getVenuesWithStockModuleActive();

        if (!mounted) return;

        setVenues(response?.data ?? []);
      } catch (error) {
        console.error(error);

        if (!mounted) return;

        setLoadError(t('stock.receipts.error.no-necessary-data'));
      } finally {
        if (mounted) {
          setIsLoadingVenues(false);
        }
      }
    }

    loadVenues();

    return () => {
      mounted = false;
    };
  }, [isVenueEditor]);

  React.useEffect(() => {
    if (!formik.values.venueId) {
      setWarehouses([]);
      return;
    }

    let mounted = true;

    async function loadWarehouses() {
      setIsLoadingWarehouses(true);

      try {
        const data = await getVenueWarehouses(formik.values.venueId);

        if (!mounted) return;

        setWarehouses(data);
      } catch (error) {
        console.error(error);
      } finally {
        if (mounted) {
          setIsLoadingWarehouses(false);
        }
      }
    }

    loadWarehouses();

    return () => {
      mounted = false;
    };
  }, [formik.values.venueId]);

  const handleVenueChange = (venueId: string) => {
    formik.setFieldValue('venueId', venueId);
    formik.setFieldValue('warehouseIds', []);
  };

  const statusOptions = React.useMemo(
    () => [
      { id: 'critical', name: t('stock.catalog.status-critical') },
      { id: 'minimum', name: t('stock.catalog.status-minimum') },
      { id: 'ok', name: t('stock.catalog.status-ok') },
    ],
    [t],
  );

  if (isLoadingVenues) {
    return <div>{t('loading')}</div>;
  }

  if (loadError) {
    return <div>{loadError}</div>;
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <SearchBar
        label={t('list-page-search-placeholder')}
        value={formik.values.searchTerm}
        onChange={(e) => formik.setFieldValue('searchTerm', e.target.value)}
        onClear={() => formik.setFieldValue('searchTerm', '')}
      />

      {!isVenueEditor && (
        <SelectField
          name="venueId"
          label="Venue"
          placeholder="Seleccione un Venue"
          value={formik.values.venueId}
          options={mapToSelectOptions(venues, { label: 'name' })}
          setFieldValue={(_, value) => handleVenueChange(value)}
          touched={formik.touched.venueId}
          error={formik.errors.venueId}
          isMulti={false}
          isSearchable
          isClearable={false}
          backspaceRemovesValue={false}
          onBlur={formik.handleBlur}
          isDisabled={isLoadingVenues}
        />
      )}
      <SelectField
        name="warehouseIds"
        label={t('stock.catalog.warehouse')}
        placeholder={t('stock.catalog.select-warehouse')}
        value={formik.values.warehouseIds}
        options={mapToSelectOptions(warehouses, { label: 'name' })}
        setFieldValue={formik.setFieldValue}
        touched={false}
        error={false}
        isMulti
        isSearchable
        isClearable
        backspaceRemovesValue={false}
        onBlur={formik.handleBlur}
        isDisabled={isLoadingWarehouses || !formik.values.venueId}
      />
      <SelectField
        name="status"
        label={t('stock.catalog.status')}
        placeholder={t('stock.catalog.status-all')}
        value={formik.values.status}
        options={mapToSelectOptions(statusOptions, { label: 'name' })}
        setFieldValue={formik.setFieldValue}
        touched={false}
        error={false}
        isMulti
        isSearchable={false}
        isClearable
        backspaceRemovesValue={false}
        onBlur={formik.handleBlur}
        isDisabled={!formik.values.venueId}
      />

      <Button
        className="btn-50 my-6"
        variant="contained"
        color="primary"
        type="submit"
        disabled={!formik.values.venueId || isLoading}
      >
        {t('search-label')}
      </Button>

      <Button
        className="btn-50 my-6"
        variant="outlined"
        color="primary"
        disabled={!formik.values.venueId || isLoading || isExporting}
        onClick={() =>
          onExport({
            venueId: formik.values.venueId,
            warehouseIds: formik.values.warehouseIds ?? [],
            searchTerm: formik.values.searchTerm.trim(),
            status: formik.values.status ?? [],
          })
        }
      >
        {isExporting ? t('loading') : t('stock.catalog.download-excel')}
      </Button>
    </form>
  );
};
